import React, { useState } from 'react';
import { X, AlertCircle, Phone, Mail, Loader2, Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const UrgentContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    urgentMessage: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact/urgent`, {
        ...formData,
        isUrgent: true
      });
      
      if (response.data.success) {
        toast.success('Demande urgente envoyée ! Nous vous recontactons sous 24-48h maximum.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          urgentMessage: ''
        });
        onClose();
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Error submitting urgent contact:', error);
      }
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-red-600 text-white p-2 rounded-lg">
              <AlertCircle size={28} />
            </div>
            <DialogTitle className="text-3xl font-bold text-black">
              Intervention <span className="text-red-600">Urgente</span>
            </DialogTitle>
          </div>
          <DialogDescription className="text-lg text-gray-600">
            Nous intervenons sous 24-48h en cas d'urgence. Remplissez le formulaire ci-dessous.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          {/* Nom */}
          <div>
            <Label htmlFor="name" className="text-base font-semibold">
              Nom complet *
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Votre nom"
              className="mt-2 h-12 text-base"
            />
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email" className="text-base font-semibold">
              Email *
            </Label>
            <div className="relative mt-2">
              <Mail className="absolute left-3 top-3.5 text-gray-400" size={20} />
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="votre@email.com"
                className="pl-11 h-12 text-base"
              />
            </div>
          </div>

          {/* Téléphone */}
          <div>
            <Label htmlFor="phone" className="text-base font-semibold">
              Téléphone *
            </Label>
            <div className="relative mt-2">
              <Phone className="absolute left-3 top-3.5 text-gray-400" size={20} />
              <Input
                id="phone"
                name="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="06 12 34 56 78"
                className="pl-11 h-12 text-base"
              />
            </div>
          </div>

          {/* Adresse d'intervention */}
          <div>
            <Label htmlFor="address" className="text-base font-semibold">
              Adresse d'intervention *
            </Label>
            <Input
              id="address"
              name="address"
              type="text"
              required
              value={formData.address}
              onChange={handleChange}
              placeholder="Adresse complète du chantier"
              className="mt-2 h-12 text-base"
            />
          </div>

          {/* Message urgent */}
          <div>
            <Label htmlFor="urgentMessage" className="text-base font-semibold">
              Nature de l'urgence *
            </Label>
            <Textarea
              id="urgentMessage"
              name="urgentMessage"
              required
              value={formData.urgentMessage}
              onChange={handleChange}
              placeholder="Décrivez la nature de l'urgence, les travaux nécessaires..."
              rows={5}
              className="mt-2 text-base"
            />
          </div>

          {/* Alerte urgence */}
          <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded-r-lg">
            <div className="flex gap-3">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={24} />
              <div>
                <p className="font-semibold text-red-900 mb-1">Demande prioritaire</p>
                <p className="text-red-800 text-sm">
                  Votre demande sera traitée en priorité. Nous vous recontacterons dans les plus brefs délais pour planifier l'intervention.
                </p>
              </div>
            </div>
          </div>

          {/* Boutons */}
          <div className="flex gap-4 pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors text-lg flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin" size={22} />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send size={22} />
                  Envoyer ma demande urgente
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-6 py-4 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
            >
              Annuler
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
